/**
 * Component: Widget Surface Allowlist
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R15
 *
 * Gate the widget runtime against the ``enabled_surfaces``
 * list returned by ``GET /api/widget/config`` (R14). A
 * deployment that has turned a surface off in the admin
 * UI must not render that surface even if the host page's
 * integration code explicitly mounts it — either via the
 * NPM ``<GalenChat />`` component or via the UMD
 * ``data-galen-surface="chat"`` attribute.
 *
 * The gate throws a typed ``WidgetSurfaceDisabledError``
 * so the init pipeline (final Batch B commit) can route
 * the failure through the documented ``onError``
 * callback and render the widget's documented error state
 * inside its container rather than silently failing or
 * partially rendering.
 *
 * Empty enabled_surfaces is treated as "nothing is
 * enabled" — a freshly-provisioned deployment has no
 * surfaces until an admin explicitly opts them in. Any
 * surface request against an empty list is rejected.
 */

import { WidgetError } from '../api/errors';
import type { WidgetSurface } from './config';

/**
 * Raised when the host integration asks the widget to
 * render a surface that is not in the deployment's
 * configured ``enabled_surfaces`` list.
 */
export class WidgetSurfaceDisabledError extends WidgetError {
  readonly requestedSurface: string;
  readonly enabledSurfaces: readonly WidgetSurface[];

  constructor(
    requestedSurface: string,
    enabledSurfaces: readonly WidgetSurface[],
  ) {
    const enabledList =
      enabledSurfaces.length === 0
        ? 'none'
        : enabledSurfaces.map((s) => `"${s}"`).join(', ');
    super(
      `Widget surface "${requestedSurface}" is not enabled on this Galen ` +
        `deployment (enabled surfaces: ${enabledList}). ` +
        'Contact your administrator to enable this surface, or remove it from the ' +
        'host integration.',
    );
    this.name = 'WidgetSurfaceDisabledError';
    this.requestedSurface = requestedSurface;
    this.enabledSurfaces = enabledSurfaces;
  }
}

/**
 * Return true if ``surface`` appears in
 * ``enabledSurfaces``. Pure helper that the init pipeline
 * uses before constructing a surface component so the
 * assertion runs even for non-throwing callers.
 */
export function isSurfaceEnabled(
  surface: string,
  enabledSurfaces: readonly WidgetSurface[],
): boolean {
  return (enabledSurfaces as readonly string[]).includes(surface);
}

/**
 * Throw ``WidgetSurfaceDisabledError`` if ``surface`` is
 * not in ``enabledSurfaces``. No-op on the happy path so
 * callers can use it as a gate at the top of a render
 * function.
 *
 * R15 compliance: this is the single choke-point through
 * which every surface render flows. The init pipeline
 * calls it; surface wrappers (Batch C) also call it in
 * case a caller bypasses the init and mounts a component
 * directly. Two levels of defence keep the "only enabled
 * surfaces render" invariant from relying on any one
 * caller remembering to check.
 */
export function assertSurfaceEnabled(
  surface: string,
  enabledSurfaces: readonly WidgetSurface[],
): void {
  if (!isSurfaceEnabled(surface, enabledSurfaces)) {
    throw new WidgetSurfaceDisabledError(surface, enabledSurfaces);
  }
}
